/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import { Library, Lock, Search, BookOpen, ClipboardList, AlertTriangle, X } from 'lucide-react';
import Page from '../../../components/Page';
import catalogSvc from '../../../services/clientAdmin/catalogServices';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META = {
    DRAFT:    { label: 'Pending Review', badge: 'bg-yellow-100 text-yellow-700', dot: false },
    LIVE:     { label: 'Live',           badge: 'bg-green-100 text-green-700',   dot: true  },
    ACTIVE:   { label: 'Live',           badge: 'bg-green-100 text-green-700',   dot: true  },
    INACTIVE: { label: 'Pending Review', badge: 'bg-yellow-100 text-yellow-700', dot: false },
    ARCHIVED: { label: 'Archived',       badge: 'bg-gray-100 text-gray-500',     dot: false },
    EXPIRED:  { label: 'Archived',       badge: 'bg-gray-100 text-gray-500',     dot: false },
};

function getMeta(code) {
    return STATUS_META[code] || { label: code || '—', badge: 'bg-gray-100 text-gray-500', dot: false };
}

function fmt(v) {
    if (!v) return '—';
    return v.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Confirmation Dialog ──────────────────────────────────────────────────────

function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel, loading }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                        <AlertTriangle size={18} className="text-amber-500" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{message}</p>
                    </div>
                </div>
                <div className="flex gap-3 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${confirmClass}`}
                    >
                        {loading ? 'Please wait…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ContentBank() {
    const [items, setItems]         = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState('');
    const [activeTab, setActiveTab] = useState('Pending Review');
    const [search, setSearch]       = useState('');

    const [confirm, setConfirm]           = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast]               = useState('');

    const load = useCallback(() => {
        setLoading(true);
        catalogSvc.getContentBank()
            .then(r => {
                if (r.ok) {
                    const data = r.data;
                    // handle both array response and { items, total } shape
                    setItems(Array.isArray(data) ? data : (data?.items || []));
                } else {
                    setError(r.data?.message || 'Failed to load content bank.');
                }
            })
            .catch(() => setError('Failed to load content bank.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // ─── Tab counts ───────────────────────────────────────────────────────────

    const counts = {
        'Pending Review': items.filter(i => getMeta(i.status).label === 'Pending Review').length,
        'Live':           items.filter(i => getMeta(i.status).label === 'Live').length,
        'Archived':       items.filter(i => getMeta(i.status).label === 'Archived').length,
        'all':            items.length,
    };

    const TABS = [
        { key: 'Pending Review', label: 'Pending Review' },
        { key: 'Live',           label: 'Live'           },
        { key: 'Archived',       label: 'Archived'       },
        { key: 'all',            label: 'All'            },
    ];

    const filtered = items.filter(item => {
        const matchTab    = activeTab === 'all' || getMeta(item.status).label === activeTab;
        const matchSearch = !search ||
            (item.title         || '').toLowerCase().includes(search.toLowerCase()) ||
            (item.contentFormat || '').toLowerCase().includes(search.toLowerCase());
        return matchTab && matchSearch;
    });

    // ─── Action handlers ──────────────────────────────────────────────────────

    const handleConfirm = async () => {
        if (!confirm) return;
        const newStatus =
        confirm.action === 'live'    ? 'LIVE'    :
        confirm.action === 'restore' ? 'DRAFT'   : 'ARCHIVED';
        setActionLoading(true);
        try {
            const r = await catalogSvc.updateContentStatus(confirm.item.itemId, confirm.item.type, newStatus);
            if (r.ok) {
                setToast(
                    confirm.action === 'live' ? `"${confirm.item.title}" is now live.` :
                        confirm.action === 'restore' ? `"${confirm.item.title}" has been restored.` :
                            `"${confirm.item.title}" has been archived.`
                );
                load();
            } else {
                setToast(r.data?.message || 'Action failed. Please try again.');
            }
        } catch {
            setToast('Action failed. Please try again.');
        } finally {
            setActionLoading(false);
            setConfirm(null);
            setTimeout(() => setToast(''), 4000);
        }
    };

    return (
        <Page title="Content Bank">
            <div className="px-6 py-6 mx-auto">

                {/* Header */}
                <div className="flex items-start gap-3 mb-5">
                    <Library size={22} className="text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Content Bank</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Review and publish your organisation's private content. Once live, it can be assigned to learners via the Catalog.
                        </p>
                    </div>
                </div>

                {/* Info banner */}
                <div className="flex items-start gap-2 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 mb-6 text-sm text-purple-800">
                    <Lock size={15} className="mt-0.5 flex-shrink-0" />
                    <span>
                        This content is private to your organisation and never visible to other tenants.{' '}
                        <span className="font-medium">
                            Content created by your Content Creators appears here for review before being made live.
                        </span>
                    </span>
                </div>

                {/* Tabs + Search */}
                <div className="flex items-center justify-between mb-4 gap-4">
                    <div className="flex gap-1 border-b border-gray-200 flex-1">
                        {TABS.map(tab => {
                            const count  = counts[tab.key] ?? 0;
                            const active = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                                        active
                                            ? 'border-purple-600 text-purple-700'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab.label}
                                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                                        active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="relative flex-shrink-0">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search content…"
                            className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-400 w-52"
                        />
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
                ) : error ? (
                    <div className="text-center py-16 text-red-500 text-sm">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 text-sm">No content found.</div>
                ) : (
                    <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Type</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Format</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Creator</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filtered.map((item, idx) => {
                                    const meta       = getMeta(item.status);                              
                                    const isPending  = item.status === 'DRAFT'; 
                                    const isLive     = item.status === 'LIVE'  || item.status === 'ACTIVE';
                                    const isArchived = item.status === 'ARCHIVED' || item.status === 'EXPIRED';

                                    return (
                                        <tr key={`${item.type}-${item.itemId}-${idx}`} className="hover:bg-gray-50/60 transition-colors">

                                            {/* Title */}
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    {item.type === 'assessment'
                                                        ? <ClipboardList size={14} className="text-purple-500 flex-shrink-0" />
                                                        : <BookOpen      size={14} className="text-blue-500 flex-shrink-0"   />}
                                                    <span className="font-medium text-gray-900 truncate max-w-[220px]">
                                                        {item.title || '—'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Content Type */}
                                            <td className="px-4 py-3.5">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    item.type === 'assessment'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {item.type === 'assessment' ? 'Assessment' : 'Course'}
                                                </span>
                                            </td>

                                            {/* Format */}
                                            <td className="px-4 py-3.5 text-gray-600 text-sm">
                                                <div>{fmt(item.contentFormat) || '—'}</div>
                                                {item.testType && (
                                                    <div className="text-xs text-gray-400">{item.testType}</div>
                                                )}
                                            </td>

                                            {/* Creator */}
                                            <td className="px-4 py-3.5 text-gray-600 text-sm">
                                                {item.creatorName || '—'}
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-3.5">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
                                                    {meta.dot && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                                                    {meta.label}
                                                </span>
                                            </td>

                                            {/* Created */}
                                            <td className="px-4 py-3.5 text-gray-500 text-xs whitespace-nowrap">
                                                {item.createdAt
                                                    ? new Date(item.createdAt).toLocaleDateString('en-GB', {
                                                        day: '2-digit', month: 'short', year: 'numeric'
                                                      })
                                                    : '—'}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-4 py-3.5 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {isPending && (
                                                        <button
                                                            onClick={() => setConfirm({ item, action: 'live' })}
                                                            className="text-xs font-medium bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors"
                                                        >
                                                            Make Live
                                                        </button>
                                                    )}
                                                    {!isArchived && (
                                                        <button
                                                            onClick={() => setConfirm({ item, action: 'archive' })}
                                                            className="text-xs font-medium text-gray-600 border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                                                        >
                                                            Archive
                                                        </button>
                                                    )}
                                                    {isArchived && (
                                                        <button
                                                            onClick={() => setConfirm({ item, action: 'restore' })}
                                                            className="text-xs font-medium text-gray-600 border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                                                        >
                                                            Restore
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                            Showing {filtered.length} of {items.length} item{items.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation dialog */}
            {confirm && (
                <ConfirmDialog
                    title={
                        confirm.action === 'live' ? 'Make Content Live' :
                            confirm.action === 'restore' ? 'Restore Content' :
                                'Archive Content'
                    }
                    message={
                        confirm.action === 'live'
                            ? `Are you sure you want to make "${confirm.item.title}" live? It will become available to assign to learners via the Catalog.`
                            : confirm.action === 'restore'
                                ? `Are you sure you want to restore "${confirm.item.title}"? It will be made live and available to assign to learners.`
                                : `Are you sure you want to archive "${confirm.item.title}"? It will no longer be visible in the Catalog.`
                    }
                    confirmLabel={
                        confirm.action === 'live' ? 'Yes, Make Live' :
                            confirm.action === 'restore' ? 'Yes, Restore' :
                                'Yes, Archive'
                    }
                    confirmClass={
                        confirm.action === 'archive'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-purple-600 hover:bg-purple-700'
                    }
                    onConfirm={handleConfirm}
                    onCancel={() => setConfirm(null)}
                    loading={actionLoading}
                />
            )}

            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-5 py-2.5 rounded-lg shadow-lg z-50 flex items-center gap-3">
                    {toast}
                    <button onClick={() => setToast('')} className="text-gray-400 hover:text-white">
                        <X size={14} />
                    </button>
                </div>
            )}
        </Page>
    );
}